// @flow
import type { DailySummary } from "../../lib/OnlineTypes";
import type { CompanyBranch } from "../../lib/Types";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import React from "react";
import { MinimalContainer } from "./Theme";
import styled, { css } from "styled-components";
import Input from "../Input";
import Checkbox from "../common/form/Checkbox";
import Button from "../Button";
import { DateRangePicker } from "react-dates";
import { fetchJson } from "../../lib/Fetching";
import moment from "moment";
import CompanyBranchDropDown from "../crm/CompanyBranchDropDown";
import DataFieldFilter from "./DataFieldFilter";
import CompanyDropDown from "../crm/CompanyDropDown";
import DataFieldsDropDown from "./DataFieldsDropDown";
import MatrixInput from "../testing/MatrixInput";
import ClientSuiteInput from "../products/ClientSuiteInput";
import TestingCategorySelect from "../sample_reception/TestingCategorySelect";

type Props = {
  company_id: ?number,
  onLoad: DailySummary => void,
  canFindCompany: boolean,
  runSearch: boolean
};

type State = {
  company_id: ?number,
  companyBranch: ?CompanyBranch,
  loading: boolean,
  product_name: ?string,
  start_date: ?Object,
  end_date: ?Object,
  startDateFocused: boolean,
  endDateFocused: boolean,
  breakdownBySuite: boolean,
  matrixId: ?number,
  clientSuiteId: ?number,
  testingCategory: ?number
};

const Container = styled(MinimalContainer)`
  padding: 20px;
  max-width: 1920px;
  margin: 0 auto;
`;

// const Row = styled.div.attrs({ className: "row" })`
//   padding-bottom: 5px;
// `;

// const FullColumn = styled.div.attrs({ className: "col-md-12" })``;
// const Column = styled.div.attrs({ className: "col-md-4" })``;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -15px;
`;

const Column = styled.div`
  flex-grow: 1;
  min-width: 350px;
  padding: 0 15px;
  margin-bottom: 5px;
`;

const FullColumn = styled.div`
  width: 100%;
  padding: 0 15px;
`;
export default class ProductFailureSampleSearch extends React.Component<Props, State> {
  state = {
    company_id: this.props.company_id,
    companyBranch: null,
    loading: false,
    product_name: "",
    start_date: moment().subtract(20, "days"),
    end_date: moment(),
    startDateFocused: false,
    endDateFocused: false,
    selectedBreakdownDataFields: [],
    breakdownBySuite: false,
    exclude_product_name: null,
    clientSuiteId: null,
    testingCategory: null
  };

  componentDidMount() {
    if (this.props.runSearch) {
      this.loadData();
    }
  }

  isValid = (): boolean => {
    const { start_date, end_date } = this.state;
    if (!start_date || !end_date) return false;
    if (end_date < start_date) return false;
    return true;
  };

  loadData = () => {
    const { onLoad } = this.props;
    const {
      company_id,
      product_name,
      exclude_product_name,
      start_date,
      end_date,
      companyBranch,
      selectedBreakdownDataFields,
      breakdownBySuite,
      matrixId,
      clientSuiteId,
      testingCategory
    } = this.state;
    this.setState({ loading: true });
    onLoad(null);

    const formParams = {
      company_id,
      form: {
        company_ids: [company_id],
        company_branch_ids: companyBranch ? [companyBranch.id] : null,
        product_name,
        exclude_product_name,
        start_date: start_date ? start_date.format("YYYY-MM-DD") : null,
        end_date: end_date ? end_date.format("YYYY-MM-DD") : null,
        data_fields: this.dataFieldFilter ? this.dataFieldFilter.queryParams() : [],
        data_field_names_breakdown: selectedBreakdownDataFields.map(o => o.value),
        breakdown_by_suite: breakdownBySuite,
        matrix_id: matrixId,
        client_suite_id: clientSuiteId,
        testing_category_id: testingCategory
      }
    };

    fetchJson("/sample_reporting/product_failure_summary.json", formParams)
      .then(json => {
        this.setState({ loading: false });
        onLoad(json.data);
      })
      .catch(e => {
        this.setState({ loading: false });
        alert("There was a problem loading this data.");
        throw e;
      });
  };

  branchChanged = (companyBranch: CompanyBranch) => {
    this.setState({
      companyBranch
    });
  };

  render() {
    const { canFindCompany } = this.props;
    const {
      company_id,
      loading,
      product_name,
      exclude_product_name,
      start_date,
      end_date,
      startDateFocused,
      endDateFocused,
      companyBranch,
      selectedBreakdownDataFields,
      breakdownBySuite,
      clientSuiteId,
      testingCategory
    } = this.state;
    return (
      <Container className="hidden-print">
        <Row>
          <Column>
            <label>{I18n.t("testing_category")}</label>
            <TestingCategorySelect
              value={testingCategory}
              onChange={o => this.setState({ testingCategory: o.value })}
            />
          </Column>
          <Column>
            <label>{I18n.t("registered_between")}</label>
            <br />
            <DateRangePicker
              startDateId="start_date"
              startDate={start_date}
              endDateId="end_date"
              endDate={end_date}
              numberOfMonths={1}
              onDatesChange={({ startDate, endDate }) =>
                this.setState({ start_date: startDate, end_date: endDate })
              }
              focusedInput={this.state.focusedInput || null}
              onFocusChange={focusedInput => this.setState({ focusedInput })}
              showClearDates={true}
              isOutsideRange={() => false}
              hideKeyboardShortcutsPanel={true}
              displayFormat="DD MMM YYYY"
            />
          </Column>
          {canFindCompany && (
            <Column>
              <label>{I18n.t("company")}</label>
              <CompanyDropDown
                onChange={company_id => this.setState({ company_id, clientSuiteId: null })}
              />
            </Column>
          )}
          <Column>
            <label>{I18n.t("product_name")}</label>
            <Input
              focus
              onPressEnter={this.loadData}
              className="form-control"
              value={product_name}
              onChange={e => this.setState({ product_name: e.target.value })}
            />
          </Column>
          {company_id && (
            <Column>
              <label>{I18n.t("select_branch")}</label>
              <CompanyBranchDropDown
                key={company_id}
                company_id={company_id}
                onBranchChange={this.branchChanged}
                value={companyBranch ? companyBranch.id : null}
                includeBlank={true}
                blankLabel={"All Branches"}
                permission_code={"view_samples"}
              />
            </Column>
          )}
          <Column>
            <label>{I18n.t("breakdown_by_attribute")}</label>
            <br />
            <DataFieldsDropDown
              values={selectedBreakdownDataFields}
              onChange={values => this.setState({ selectedBreakdownDataFields: values })}
              company_id={company_id}
              company_branch_id={companyBranch ? companyBranch.id : null}
            />
          </Column>
          <Column>
            <label>{I18n.t("matrix")}</label>
            <MatrixInput
              multiple={false}
              onChange={option => this.setState({ matrixId: option ? option.value : null })}
              defaultStyle
            />
          </Column>
          <Column>
            <label>{I18n.t("exclude_product_name")}</label>
            <Input
              focus
              onPressEnter={this.loadData}
              className="form-control"
              value={exclude_product_name}
              onChange={e => this.setState({ exclude_product_name: e.target.value })}
              placeholder={
                "e.g. add 'Shelf,Wash' to ignore products with 'Shelf' & 'Wash' in name"
              }
            />
          </Column>
          {company_id && (
            <Column>
              <label>{I18n.t("client_suite")}</label>
              <ClientSuiteInput
                company_id={company_id}
                multiple={false}
                value={clientSuiteId}
                onChange={option =>
                  this.setState({ clientSuiteId: option ? option.value : null })
                }
              />
            </Column>
          )}
        </Row>
        <Row>
          <FullColumn style={{ marginBottom: 0, paddingBottom: 0, marginTop: 10 }}>
            <label>
              <Checkbox
                checked={breakdownBySuite}
                onChange={e => this.setState({ breakdownBySuite: e.target.checked })}
              />
              &nbsp;
              {I18n.t("breakdown_by_suite")}
            </label>
          </FullColumn>
        </Row>
        {company_id && (
          <Row style={{ paddingBottom: 0, paddingTop: 5 }}>
            <FullColumn>
              <DataFieldFilter
                key={company_id}
                ref={ref => (this.dataFieldFilter = ref)}
                onSubmit={this.loadData}
                company_id={company_id}
                company_branch_id={companyBranch ? companyBranch.id : null}
              />
            </FullColumn>
          </Row>
        )}
        <Row>
          <Column style={{ marginBottom: 0, paddingBottom: 0 }}>
            <Button
              disabled={!this.isValid()}
              title={loading ? I18n.t("loading") : I18n.t("view_summary")}
              icon="fa-search"
              onClick={this.loadData}
            />
          </Column>
        </Row>
      </Container>
    );
  }
}
